import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchBestDealsTopics, fetchBestDealsTopic } from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import useSeo from "@/lib/useSeo";
import { ArrowRight, ArrowLeft, Tag } from "lucide-react";

export default function BestDeals() {
  const { topic } = useParams();
  const isIndex = !topic;
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, err: null });
    const fetcher = topic ? fetchBestDealsTopic(topic) : fetchBestDealsTopics();
    fetcher
      .then((data) => alive && setState({ loading: false, data, err: null }))
      .catch((e) =>
        alive && setState({
          loading: false,
          data: null,
          err: e?.response?.data?.detail || "Couldn't load",
        })
      );
    return () => { alive = false; };
  }, [topic]);

  // SINGLE useSeo call — pick the right SEO based on whether we're on the
  // index or a specific topic. React hooks rules require unconditional call order.
  const topics = isIndex ? state.data || [] : [];
  const data = isIndex ? null : state.data;

  useSeo({
    title: isIndex
      ? "Best Deals & Coupons Right Now — The Couponer"
      : data
      ? `${data.title} — ${data.count}+ verified deals · The Couponer`
      : "Best Deals · The Couponer",
    description: isIndex
      ? "The Couponer's hand-curated best-deal lists. Browse by category — free shipping, 50%+ off, BOGO, clearance, freebies, and today's newest deals. Updated in real time."
      : data
      ? `${data.hero} Browse ${data.count} verified deals in this category, refreshed daily.`
      : undefined,
    canonical: isIndex
      ? "https://thecouponer.com/best-deals"
      : `https://thecouponer.com/best-deals/${topic}`,
    keywords: isIndex
      ? "best deals, best coupons, top coupons, hot deals, best promo codes, free shipping deals, 50 off, bogo deals, clearance, free stuff"
      : data
      ? `${data.title.toLowerCase()}, best ${data.title.toLowerCase()}, top ${data.title.toLowerCase()}, curated deals, verified coupons`
      : undefined,
    jsonLd: isIndex
      ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Best Deals & Coupons — The Couponer",
          "url": "https://thecouponer.com/best-deals",
          "hasPart": topics.map((t) => ({
            "@type": "CollectionPage",
            "name": t.title,
            "url": `https://thecouponer.com/best-deals/${t.slug}`,
            "description": t.hero,
          })),
        }
      : data
      ? [
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thecouponer.com/" },
              { "@type": "ListItem", "position": 2, "name": "Best Deals", "item": "https://thecouponer.com/best-deals" },
              { "@type": "ListItem", "position": 3, "name": data.title, "item": `https://thecouponer.com/best-deals/${topic}` },
            ],
          },
          {
            "@type": "ItemList",
            "name": data.title,
            "numberOfItems": data.count,
            "itemListElement": (data.coupons || []).slice(0, 20).map((c, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": {
                "@type": "Offer",
                "name": c.title,
                "url": `https://thecouponer.com/coupon/${c.slug || c.id}`,
                "seller": { "@type": "Organization", "name": c.store },
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
              },
            })),
          },
        ]
      : undefined,
  });

  // ---- INDEX view: /best-deals ----
  if (isIndex) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10" data-testid="best-deals-index">
        <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-2 inline-flex items-center gap-2">
          <Tag className="w-3 h-3" /> CURATED · UPDATED IN REAL TIME
        </div>
        <h1 className="serif text-5xl md:text-7xl leading-[0.95] mb-3">
          Best deals, <span className="italic text-[#ff5b48]">by the pound.</span>
        </h1>
        <p className="text-lg text-[#3a3a38] max-w-2xl leading-relaxed mb-10">
          Every list below is auto-curated from our 14,000+ live deals. Pick a shelf, grab
          what looks good.
        </p>

        {state.loading && (
          <div className="mono text-xs tracking-widest text-[#5c5c5c]">LOADING TOPICS…</div>
        )}

        {topics.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topics.map((t) => (
              <Link
                key={t.slug}
                to={`/best-deals/${t.slug}`}
                data-testid={`best-deals-topic-${t.slug}`}
                className="group border-2 border-[#2d2a32] rounded-2xl p-5 bg-white hard-shadow-sm hover:bg-[#fde68a] transition-colors"
              >
                <div className="flex items-baseline justify-between mb-1">
                  <h2 className="serif text-2xl leading-tight text-[#2d2a32] group-hover:text-[#ff5b48]">
                    {t.title}
                  </h2>
                  <span className="mono text-[10px] tracking-[0.25em] text-[#5c5c5c] shrink-0 ml-2">
                    {t.count.toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-[#3a3a38] leading-snug mb-4">{t.hero}</p>
                <span className="mono text-[10px] tracking-[0.25em] text-[#2d2a32] inline-flex items-center gap-1 font-bold">
                  BROWSE ALL <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- TOPIC view: /best-deals/:topic ----
  if (state.loading) return <div className="mono text-xs p-10">LOADING…</div>;
  if (state.err || !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="serif text-4xl mb-3">Topic not found.</p>
        <Link to="/best-deals" className="mono text-xs tracking-widest underline">
          BROWSE ALL BEST-DEAL TOPICS
        </Link>
      </div>
    );
  }

  const titleWords = data.title.split(" ");
  const lastWord = titleWords[titleWords.length - 1];
  const restWords = titleWords.slice(0, -1).join(" ");

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid={`best-deals-topic-page-${topic}`}>
      <Link
        to="/best-deals"
        className="text-[10px] tracking-[0.3em] text-[#5c5c5c] hover:text-[#ff6b6b] mb-4 inline-flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> ALL TOPICS
      </Link>
      <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-1 inline-flex items-center gap-2">
        <Tag className="w-3 h-3" /> {data.count.toLocaleString()} DEALS · UPDATED DAILY
      </div>
      <h1 className="serif text-5xl md:text-7xl leading-[0.95] mb-2">
        {restWords} <span className="italic text-[#ff5b48]">{lastWord}</span>
      </h1>
      <p className="text-lg text-[#3a3a38] max-w-2xl leading-relaxed mb-8">{data.hero}</p>

      {data.coupons?.length === 0 ? (
        <div className="border-2 border-dashed border-[#2d2a32]/40 rounded-xl p-6 bg-white text-center">
          <p className="text-sm text-[#3a3a38]">
            No active deals in this topic yet — check back tomorrow, fresh coupons arrive every few
            minutes.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.coupons.map((c) => (
            <CouponCard key={c.id} coupon={c} />
          ))}
        </div>
      )}
    </div>
  );
}
